import React, {
	useEffect,
	memo,
	useContext,
	useMemo,
	useCallback,
	useRef,
} from "react";
import { isEmpty } from "lodash";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useStore } from "react-redux";
import { useParams } from "react-router-dom";
import { Field, change, FormSection, getFormSyncWarnings } from "redux-form";

import {
	AddressSectionRedux,
	CheckButton,
	CustomDropdownByValue,
	CustomInput,
	PhoneInputRedux,
	SpinnerLoader,
	Tooltip,
	WebsitePreview,
} from "components";
import { ApplicationEntryProgressContext } from "components/contexts";
import {
	AchBankInformation,
	HowReceivePaymentAuth,
	StatementSection,
	WhoAreYourCustomers,
} from "components/formSections";
import BusinessUnderwritingInfoPartTwo from "components/formSections/businessUnderwritingInfoPartTwo";
import RadioButton from "components/radioButton";
import { AddressSectionFields, ValidationMessages } from "globalConstants";
import { TerminalGatewayTypes, TypeOfBusinessValues } from "types";
import {
	businessDateNormalizer,
	taxIdNormalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import { maxPhoneLength15, phoneMinLength8 } from "validators/userValidators";
import {
	dateValidation,
	emailValid,
	maxLength100,
	maxLength2,
	maxLengthTaxId,
	minLength2,
	minLengthTaxId,
	normalizeAll,
	onlyAlphanumericAndCustomSpecialCharacters,
	onlyAlphanumericAndSpecialCharacters,
	onlyDate,
	onlyDigits,
	required,
	validateWhenTypeOfBusinessIsOther,
	webAddressRegex,
} from "validators/validation";

import AcceptCardsSection from "./acceptCardsSection";
import BankInformation from "./bankInformation";
import BusinessUnderwritingInfo from "./businessUnderwritingInfo";
import DaysToDelivery from "./daysToDelivery";
import DesiredLimits from "./desiredLimits";
import IsItNewMerchant from "./isItNewMerchant";
import PaperStatement from "./paperStatement";
import RefundPolicy from "./refundPolicy";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "./businessInfo.module.scss";

const BusinessInfoFields = ({
	applicationId,
	isPaperApplication,
	formName,
	feesFormName,
	banks,
	billingBanks,
	setBanks,
	setBillingBanks,
	dropDownHeight,
	formValues,
	websiteInformation,
	websiteError = "",
	isMPAPage,
	hiddenFieldsOnMPA,
	isEditForm,
	achProcessor,
	isWebSiteValidationPending,
	websiteUpdatedValue,
	...props
}) => {
	const dispatch = useDispatch();
	const params = useParams();

	const { immediateSave } = useContext(ApplicationEntryProgressContext);

	const store = useStore();
	const state = store.getState();
	const warnings = getFormSyncWarnings(formName)(state);

	useEffect(() => {
		if (props.submitting) props.showSpinnerOverlay();
		return props.hideSpinnerOverlay;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.submitting]);

	useEffect(() => {
		dispatch(change(formName, "achProcessorHidden", achProcessor));
	}, [formName, achProcessor, dispatch]);

	const websiteValidation = useMemo(
		() => (+formValues?.eCommerce > 0 ? [required] : []),
		[formValues?.eCommerce],
	);

	const useThirdPartyFulfillment = useMemo(
		() => formValues?.doYouUseAThirdPartyForFulfillment === "true",
		[formValues?.doYouUseAThirdPartyForFulfillment],
	);

	const taxIdLabel =
		formValues?.typeOfBusiness === "SoleProp" ? "SSN" : "Tax ID";

	const getDaysToDelivery = () =>
		+formValues?.keyed || +formValues?.eCommerce ? (
			<DaysToDelivery isPaperApplication={isPaperApplication} />
		) : null;

	const incorrectUrl = !webAddressRegex.test(formValues?.website);

	const websiteTooltipDisabled =
		incorrectUrl ||
		!websiteInformation?.checkedSuccessfully ||
		isWebSiteValidationPending;

	const handleWebSiteCheck = useCallback(() => {
		if (!fp.isEmpty(formValues?.website))
			Promise.all([
				immediateSave?.(),
				props.webSiteCheck(formValues?.website),
			]).then(([data, webSiteInfo]) => {
				if (!params.id && !isMPAPage) {
					// only for App Entry
					props.updateWebSiteInformation?.(
						data?.applicationId,
						webSiteInfo,
					);
				}
			});
		else return;
	}, [formValues?.website, immediateSave, isMPAPage, params.id, props]);

	const prevValueRef = useRef(formValues?.website);

	useEffect(() => {
		if (prevValueRef.current !== websiteUpdatedValue) {
			handleWebSiteCheck();
		}

		return () => {
			prevValueRef.current = websiteUpdatedValue;
		};
	}, [handleWebSiteCheck, websiteUpdatedValue]);

	const typeOfBusinessValues = TypeOfBusinessValues.getOptions().map(
		item => ({
			id: item.value,
			name: item.displayName,
			value: item.name,
		}),
	);

	const gatewayValues = props.gatewayValues;

	const showCardNetworkTokenization =
		gatewayValues?.isTsysTokenizationEnabled === "true" &&
		gatewayValues?.gatewayName === TerminalGatewayTypes.ARISE.name;

	return (
		<>
			<Field
				component="input"
				name="hidden"
				value="1"
				className={styles.hidden}
			/>
			<Field
				component="input"
				type="hidden"
				name="achProcessorHidden"
				disabled
			/>
			{!hiddenFieldsOnMPA?.includes("isItNewMerchant") && (
				<>
					<IsItNewMerchant />
					<hr className="line" />
				</>
			)}
			<Row>
				<Col lg={5}>
					<Field
						component={CustomInput}
						label="DBA Name"
						validate={[required, minLength2, maxLength100]}
						normalize={normalizeAll([
							onlyAlphanumericAndCustomSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						normalizeOnBlur={trimNormalizer}
						type="text"
						name="dbaName"
					/>
				</Col>
				<Col lg={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						label="Legal Name"
						type="text"
						validate={[required, minLength2, maxLength100]}
						normalize={normalizeAll([
							onlyAlphanumericAndCustomSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						normalizeOnBlur={trimNormalizer}
						name="legalName"
						loader={SpinnerLoader}
					/>
				</Col>
			</Row>
			<Row>
				<Col lg={5}>
					<Tooltip
						html={
							<WebsitePreview
								websiteInformation={websiteInformation}
							/>
						}
						disabled={websiteTooltipDisabled}
						interactive
						useContext
						title="&nbsp;"
						position="top"
					>
						<Field
							label="Website"
							component={CustomInput}
							validate={websiteValidation}
							type="text"
							name="website"
							normalize={trimMidSentenceNormalizer}
							normalizeOnBlur={trimNormalizer}
							disabled={isWebSiteValidationPending}
							loader={SpinnerLoader}
							onBlur={handleWebSiteCheck}
							disableTriggerSave
							loading={isWebSiteValidationPending}
							hideRequiredMark={websiteValidation.length === 0}
							inputIcon={
								isEmpty(formValues?.website) &&
								showCardNetworkTokenization && (
									<WarningIcon
										className="text-warning"
										width="22"
									/>
								)
							}
							inputIconMessage={
								ValidationMessages.NetworkTokenizationWebsiteWarning
							}
						/>
					</Tooltip>
					{websiteError && (
						<Row className={styles.wrapWebsiteError}>
							<Col sm={{ span: 6, offset: 6 }}>
								<div className={styles.warning}>
									{websiteError}
								</div>
							</Col>
						</Row>
					)}
				</Col>

				{!hiddenFieldsOnMPA?.includes("fax") && (
					<Col lg={{ span: 5, offset: 2 }}>
						<Field
							wrapperClassName={styles.fax}
							component={PhoneInputRedux}
							label="Fax"
							name="fax"
							validate={[phoneMinLength8, maxPhoneLength15]}
							formName={formName}
						/>
					</Col>
				)}
			</Row>
			<Row>
				<Col lg={5}>
					<Field
						component={PhoneInputRedux}
						label="Business Phone"
						name="businessPhone"
						isRequired
						validate={[required, phoneMinLength8, maxPhoneLength15]}
						formName={formName}
					/>
				</Col>
				{hiddenFieldsOnMPA?.includes("customerService") && (
					<Col
						lg={{
							span: 5,
							offset: 2,
						}}
					>
						<Field
							component={CustomInput}
							label="Business Email"
							validate={[emailValid, required]}
							normalizeOnBlur={trimNormalizer}
							normalize={trimMidSentenceNormalizer}
							type="email"
							name="businessEmail"
						/>
					</Col>
				)}
			</Row>
			<Row>
				{!hiddenFieldsOnMPA?.includes("customerService") && (
					<>
						<Col lg={5}>
							<Field
								component={PhoneInputRedux}
								label="Customer Service Phone"
								name="csPhone"
								validate={[phoneMinLength8, maxPhoneLength15]}
								formName={formName}
							/>
						</Col>
						<Col
							lg={{
								span: 5,
								offset: !hiddenFieldsOnMPA?.includes(
									"customerService",
								)
									? 2
									: 0,
							}}
						>
							<Field
								component={CustomInput}
								label="Business Email"
								validate={[emailValid, required]}
								normalizeOnBlur={trimNormalizer}
								normalize={trimMidSentenceNormalizer}
								type="email"
								name="businessEmail"
							/>
						</Col>
					</>
				)}
			</Row>

			{!hiddenFieldsOnMPA?.includes(
				"commercialMarketingCommunications",
			) && (
				<Row className="mb-3">
					<Col>
						<Field
							component={CheckButton}
							label="By checking this box, Merchant opts out of receiving future commercial marketing communications from American Express."
							name="isMerchantOptsOutOfReceivingFutureCommercialFromAmex"
							id="isMerchantOptsOutOfReceivingFutureCommercialFromAmex"
							hideRequiredMark
						/>
					</Col>
				</Row>
			)}
			<Row>
				{!hiddenFieldsOnMPA?.includes("typeofBusiness") && (
					<Col lg={5}>
						<>
							<Field
								component={CustomDropdownByValue}
								label="Type of Business"
								validate={[required]}
								name="typeOfBusiness"
								selected={formValues?.typeOfBusiness}
								items={typeOfBusinessValues}
							/>
							{formValues?.typeOfBusiness ===
							TypeOfBusinessValues.Other.name ? (
								<>
									<Field
										component={CustomInput}
										label="Other Type of Business"
										disabled={
											formValues?.typeOfBusiness !==
											TypeOfBusinessValues.Other.name
										}
										normalize={normalizeAll([
											onlyAlphanumericAndSpecialCharacters,
											trimMidSentenceNormalizer,
										])}
										validate={[
											validateWhenTypeOfBusinessIsOther,
										]}
										type="text"
										name="businessIfOther"
										normalizeOnBlur={trimNormalizer}
									/>
								</>
							) : null}
						</>
					</Col>
				)}
				{!hiddenFieldsOnMPA?.includes("noOfLocations") && (
					<Col lg={{ span: 5, offset: 2 }}>
						<Field
							component={CustomInput}
							label="No. of Locations"
							normalize={onlyDigits}
							validate={fp.compact([
								!isPaperApplication ? required : undefined,
								maxLength2,
							])}
							name="noOfLocations"
							hideRequiredMark={isPaperApplication}
						/>
					</Col>
				)}
				<Col
					lg={{
						span: 5,
						offset: !hiddenFieldsOnMPA?.includes("noOfLocations")
							? 0
							: 2,
					}}
				>
					<Field
						label="Business Established Date"
						component={CustomInput}
						type="text"
						normalize={onlyDate}
						normalizeOnBlur={businessDateNormalizer}
						name="businessEstablishedDate"
						placeholder="MM/DD/YYYY"
						validate={fp.compact([
							!isPaperApplication ? required : undefined,
							dateValidation,
						])}
						hideRequiredMark={isPaperApplication}
					/>
				</Col>
			</Row>

			<Row>
				<Col lg={5}>
					<div style={{ marginBottom: "20px" }}>
						<Field
							component={CustomInput}
							type="text"
							label={taxIdLabel}
							validate={[
								required,
								minLengthTaxId,
								maxLengthTaxId,
							]}
							normalize={taxIdNormalizer}
							name="taxId"
							hideRequiredMark={false}
						/>
					</div>
				</Col>
			</Row>

			<hr className="line" />
			<div className="mb-4 d-flex align-items-center">
				<p className={`${styles.subHeader} mb-0`}>
					Where is the location address?
				</p>
			</div>
			<FormSection name="businessAddress">
				<AddressSectionRedux
					formName={formName}
					sectionName="businessAddress.address"
					isRequired
					hasTypeOfLocation
					typeOfLocationValue={
						formValues?.businessAddress?.address?.[
							AddressSectionFields.TypeOfLocation.name
						]
					}
				/>
			</FormSection>
			<hr className="line" />
			<FormSection name="legalAddress">
				<AddressSectionRedux
					formName={formName}
					sectionName="legalAddress.address"
					isRequired
					isAddressTheSameLabel="Is legal address the same?"
					isAddressTheSameName="isLegalAddressTheSame"
					isAddressTheSameValue={
						formValues?.legalAddress?.isLegalAddressTheSame
					}
				/>
			</FormSection>
			<hr className="line" />
			{!hiddenFieldsOnMPA?.includes("mailingLocationSection") && (
				<Row>
					<Col>
						<div className={`${styles.addressTitle} mb-4`}>
							Address for Documentation to be Mailed
						</div>
						<div className={styles.wrappRadio}>
							<Field
								component={RadioButton}
								value="legal_address"
								className={styles.radioButton}
								name="documentationMailingAddress"
								label="Legal Address"
								type="radio"
								id="documentationMailingAddress_legal"
							/>
							<Field
								component={RadioButton}
								value="dba_address"
								className={styles.radioButton}
								name="documentationMailingAddress"
								label="DBA Address"
								type="radio"
								id="documentationMailingAddress_dba"
							/>
						</div>
					</Col>
				</Row>
			)}

			{!hiddenFieldsOnMPA?.includes("paperStatement") && (
				<PaperStatement />
			)}

			{(!hiddenFieldsOnMPA?.includes("mailingLocationSection") ||
				!hiddenFieldsOnMPA?.includes("paperStatement")) && (
				<hr className="line" />
			)}

			<AcceptCardsSection hiddenFieldsOnMPA={hiddenFieldsOnMPA} />
			<hr className="line" />

			<DesiredLimits
				formName={formName}
				formValues={formValues}
				feesFormName={feesFormName}
				isEditForm={isEditForm}
				achProcessor={achProcessor}
			/>
			<hr className="line" />

			{achProcessor && (
				<>
					<WhoAreYourCustomers formValues={formValues} />
					<HowReceivePaymentAuth formValues={formValues} />
					<hr className="line" />
				</>
			)}

			{!hiddenFieldsOnMPA?.includes("refundPolicySection") && (
				<>
					<RefundPolicy formValues={formValues} />
					<hr className="line" />
				</>
			)}
			{!hiddenFieldsOnMPA?.includes(
				"businessUnderwritingInfoSection",
			) && <BusinessUnderwritingInfo formValues={formValues} />}

			{!hiddenFieldsOnMPA?.includes("daysToDeliverySection") &&
				getDaysToDelivery()}

			{!hiddenFieldsOnMPA?.includes(
				"businessUnderwritingInfoSection",
			) && (
				<>
					<BusinessUnderwritingInfoPartTwo
						formValues={formValues}
						useThirdPartyFulfillment={useThirdPartyFulfillment}
					/>
					<hr className="line" />
				</>
			)}

			{achProcessor && <StatementSection isRequired />}

			<BankInformation
				banks={banks}
				setBanks={setBanks}
				formName={formName}
				dropDownHeight={dropDownHeight}
				menuPlacement={props.menuPlacement}
				formValues={formValues}
				routingExists={!warnings?.routing}
				hasTitle
			/>

			{achProcessor && (
				<>
					<hr className="line" />
					<AchBankInformation
						banks={billingBanks}
						setBanks={setBillingBanks}
						formName={formName}
						dropDownHeight={dropDownHeight}
						menuPlacement={props.menuPlacement}
						formValues={formValues}
						routingExists={!warnings?.billingRouting}
					/>
				</>
			)}
		</>
	);
};

const component = memo(BusinessInfoFields);
export { component as BusinessInfoFields };
